"use client";

import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  type Chain,
  type HttpTransport,
  type PrivateKeyAccount,
  TypedDataParameter,
  type WalletClient,
} from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { TradeResponse } from "#/types/orders";
import { KyanError } from "#/types/service";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import { EIP712Domain, withSignatureErrorHandler } from "#/utils/signature";

import { rfqFill } from "../kyan.service";
import { type RFQFillRequest, type WithoutSignature } from "../kyan.types";
import { defaultApiResponseHandler, rfqOrderErrorHandler } from "../kyan.utils";
import { useTradingSession } from "./useTradingSession";

export const FillRFQType: TypedDataParameter[] = [
  { name: "deadline", type: "uint256" },
  { name: "taker", type: "address" },
  { name: "responseId", type: "string" },
];

const signRFQFill = async (
  fillRequest: Omit<RFQFillRequest, "signature" | "signature_deadline">,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<RFQFillRequest> => {
  const signature_deadline = getSignatureDeadline();

  return {
    ...fillRequest,
    signature_deadline,
    signature: await walletClient.signTypedData({
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { FillRFQType },
      primaryType: "FillRFQType",
      message: {
        taker: fillRequest.taker,
        responseId: fillRequest.response_id,
        deadline: signature_deadline,
      },
    }),
  };
};

const validateRFQFillSignature = async (
  payload: RFQFillRequest,
  chainId: ChainId,
): Promise<boolean> => {
  const publicClient = getPublicClient(chainId);

  return await publicClient.verifyTypedData({
    address: payload.taker,
    domain: EIP712Domain[chainId],
    types: { FillRFQType },
    primaryType: "FillRFQType",
    message: {
      deadline: payload.signature_deadline,
      taker: payload.taker,
      responseId: payload.response_id,
    },
    signature: payload.signature,
  });
};

export const useRFQFill = (
  options?: UseMutationOptions<
    TradeResponse,
    Error,
    Pick<RFQFillRequest, "taker" | "response_id">
  > & {
    onPendingFill?: (signedFill: RFQFillRequest) => void;
  },
) => {
  const { walletClient } = useWalletClient();
  const { shouldUseSessionHash } = useTradingSession();

  return useMutation({
    ...options,
    mutationFn: async ({ taker, response_id }) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const serviceName = "Rfq Fill";
      const eventHandlers = {
        onSuccess: (response: TradeResponse) => {
          rfqOrderErrorHandler({ serviceName, response });
        },
        onError: (error: KyanError) => {
          rfqOrderErrorHandler({ serviceName, error });
        },
      };

      const data: WithoutSignature<RFQFillRequest> = {
        taker,
        response_id,
      };

      if (shouldUseSessionHash) {
        return defaultApiResponseHandler({
          serviceName,
          payload: { data, includeSessionHash: true },
          service: rfqFill,
          ...eventHandlers,
        });
      }

      const signedFill = await withSignatureErrorHandler(async () =>
        signRFQFill(
          data,
          walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
        ),
      );

      const isSignatureValid = await validateRFQFillSignature(
        signedFill,
        walletClient.chain?.id as ChainId,
      );

      options?.onPendingFill?.(signedFill);

      const response = await defaultApiResponseHandler({
        serviceName,
        payload: { data: signedFill },
        service: rfqFill,
        validation: [isSignatureValid],
        ...eventHandlers,
      });

      return response;
    },
  });
};
